// src/features/apps/legal/useAppLegal.ts

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { APP_QUERY_KEYS } from '@/lib/constants/app';
import { AppApi } from '../AppApi';
import type { AppLegalAdoptReq } from '@/lib/types/AppLegal';

/**
 * The app's adopted privacy-policy versions (current + history). Fails
 * quietly like the factory reads: an api without the route (pre-roll) or an
 * app with no web row simply shows the policy as missing.
 */
export function useAppLegal(appCfgId: number, enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_QUERY_KEYS.legal(selectedCId ?? 0, appCfgId),
    queryFn: () => AppApi.getLegal(selectedCId!, appCfgId),
    enabled: enabled && selectedCId != null && appCfgId > 0,
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** Adopt a rendered policy: the server stamps who/when/IP and flips the listing URL. */
export function useAdoptLegal(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: AppLegalAdoptReq) => AppApi.adoptLegal(selectedCId!, appCfgId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.legal(selectedCId!, appCfgId) });
      // The listing's privacy URL changed with it.
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId) });
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
    },
  });
}
